<template>
  <div>
    <h2 id="claim-credit-methods">{{ $t("components.payment_method.bank_details_form.title") }}</h2>

    <i18n path="components.payment_method.bank_details_form.description" tag="p">
      <template #email>
        <a
          :href="
            'mailto:info@locomotion.app?subject=' +
            $t('components.payment_method.bank_details_form.email_subject') +
            '&body=' +
            $t('components.payment_method.bank_details_form.email_body')
          "
          >info@locomotion.app</a
        >
      </template>
    </i18n>

    <img
      src="/cheque.png"
      :alt="$t('components.payment_method.bank_details_form.cheque_example')"
      class="mx-auto d-block mb-2 mw-100"
    />
    <ActionStep :number="1" slim>
      <template #title>
        <span
          >{{ $t("components.payment_method.bank_details_form.steps.cheque_number.title") }}
          {{
            $t("components.payment_method.bank_details_form.steps.cheque_number.not_required")
          }}</span
        >
      </template>
    </ActionStep>
    <validation-observer ref="profileFormObserver" v-slot="{ passes, pristine, valid }">
      <b-form @submit.prevent.stop="passes(() => submit(!pristine))">
        <ActionStep :number="2" slim>
          <template #title>
            <forms-validated-input
              v-model="bankTransitNumber"
              class="flex-fill"
              name="bank_transit_number"
              :label="$t('components.payment_method.bank_details_form.steps.transit_number.title')"
              :rules="{ required: true, length: 5, numeric: true }"
              type="text"
              size="5"
            />
          </template>
        </ActionStep>

        <ActionStep :number="3" slim>
          <template #title>
            <forms-validated-input
              v-model="bankInstitutionNumber"
              name="bank_institution_number"
              class="flex-fill"
              :label="
                $t('components.payment_method.bank_details_form.steps.institution_number.title')
              "
              :rules="{ required: true, length: 3, numeric: true }"
              type="text"
              size="3"
            />
          </template>
        </ActionStep>

        <ActionStep :number="4" slim>
          <template #title>
            <forms-validated-input
              v-model="bankAccountNumber"
              name="bank_account_number"
              class="flex-fill"
              :label="$t('components.payment_method.bank_details_form.steps.account_number.title')"
              :rules="{ required: true, min: 7, max: 12, numeric: true }"
              type="text"
            />
          </template>
        </ActionStep>

        <p>{{ $t("components.payment_method.bank_details_form.processing_time") }}</p>

        <div class="form__buttons">
          <icon-button
            variant="success"
            type="submit"
            :disabled="!valid && pristine"
            :loading="loading || showLoading"
            :icon="saveButtonIcon"
          >
            {{ saveButtonLabel || $t("forms.save") | capitalize }}
          </icon-button>
          <template v-if="hadBankInfoInitially && showReset">
            &nbsp;
            <icon-button icon="trash" variant="outline-danger" :onclick="resetBankInfo">
              {{ $t("components.payment_method.bank_details_form.buttons.clear") }}
            </icon-button>
          </template>
          <template v-if="showCancel">
            &nbsp;
            <icon-button role="cancel" @click="$emit('cancel')">{{
              $t("forms.cancel") | capitalize
            }}</icon-button>
          </template>
        </div>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import FormsValidatedInput from "@/components/Forms/ValidatedInput.vue";
import IconButton from "@/components/shared/IconButton.vue";
import ActionStep from "@/components/shared/Step.vue";
import { put } from "@/requests/server";
import Vue from "vue";

export default Vue.extend({
  name: "BankDetailsForm",
  components: { IconButton, ActionStep, FormsValidatedInput },
  props: {
    showCancel: {
      type: Boolean,
      default: false,
    },
    saveButtonLabel: {
      type: String,
      default: undefined,
    },
    saveButtonIcon: {
      type: String,
      default: "file-earmark-arrow-down",
    },
    showLoading: {
      type: Boolean,
      default: false,
    },
    showReset: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const user = this.$store.state.user;
    return {
      loading: false,
      hadBankInfoInitially:
        user.bank_transit_number ||
        user.bank_institution_number ||
        user.bank_account_number ||
        false,
      bankTransitNumber: user.bank_transit_number || null,
      bankInstitutionNumber: user.bank_institution_number || null,
      bankAccountNumber: user.bank_account_number || null,
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
  },
  methods: {
    async resetBankInfo() {
      this.bankAccountNumber = null;
      this.bankTransitNumber = null;
      this.bankInstitutionNumber = null;

      await this.submit(true);
    },
    async submit(changed) {
      if (changed) {
        this.loading = true;
        await put(
          `users/${this.user.id}`,
          {
            id: this.user.id,
            bank_transit_number: this.bankTransitNumber,
            bank_institution_number: this.bankInstitutionNumber,
            bank_account_number: this.bankAccountNumber,
          },
          {
            cleanupCallback: () => (this.loading = false),
            notifications: { action: "Mise à jour des coordonnées bancaires" },
          }
        );

        this.$store.commit("user", {
          ...this.user,
          bank_account_number: this.bankAccountNumber,
          bank_transit_number: this.bankTransitNumber,
          bank_institution_number: this.bankInstitutionNumber,
        });
      }
      this.$emit("saved");
    },
  },
});
</script>

<style scoped lang="scss"></style>
